import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, ArrowLeft, Clock, CalendarDays, BookOpenCheck, Languages, Layers } from "lucide-react";

export default function CourseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);

  useEffect(() => { api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => navigate("/courses")); }, [slug, navigate]);

  if (!course) return <div className="min-h-[60vh] flex items-center justify-center font-mono text-clay text-sm">加载中…</div>;

  const enroll = () => {
    if (!user) { navigate("/login", { state: { from: `/checkout/${slug}` } }); return; }
    navigate(`/checkout/${slug}`);
  };

  const facts = [
    { icon: CalendarDays, label: "Duration", value: course.duration },
    { icon: Clock, label: "Total hours", value: course.hours },
    { icon: BookOpenCheck, label: "Sessions", value: course.sessions },
    { icon: Layers, label: "Vocabulary", value: course.vocab },
    { icon: Languages, label: "CEFR", value: course.cefr },
    { icon: BookOpenCheck, label: "Format", value: course.format },
  ].filter((f) => f.value);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <Link to="/courses" className="inline-flex items-center gap-2 text-sm text-clay hover:text-cinnabar transition-colors mb-8">
        <ArrowLeft size={16} /> All courses
      </Link>
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <span className="label text-cinnabar">{course.level} · {course.hsk}</span>
          <h1 className="font-serif font-light text-5xl sm:text-6xl tracking-tighter mt-3">{course.title}</h1>
          <p className="text-lg text-clay mt-6 leading-relaxed">{course.description}</p>

          {facts.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 border-t border-l border-black/10 mt-10">
              {facts.map((f, i) => (
                <div key={i} className="border-r border-b border-black/10 p-4" data-testid={`fact-${f.label.toLowerCase().replace(/ /g, "-")}`}>
                  <f.icon size={16} className="text-cinnabar mb-2" />
                  <p className="label text-clay">{f.label}</p>
                  <p className="text-sm text-ink font-medium mt-0.5">{f.value}</p>
                </div>
              ))}
            </div>
          )}

          <h2 className="font-serif text-3xl mt-12 mb-6">Course syllabus</h2>
          <ul className="border-t border-black/10">
            {course.syllabus.map((s, i) => (
              <li key={i} className="flex items-center gap-4 py-4 border-b border-black/10">
                <span className="w-6 h-6 bg-cinnabar text-rice flex items-center justify-center shrink-0 font-mono text-xs">{i + 1}</span>
                <span className="text-ink">{s}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5">
          <div className="border border-black/10 bg-white sticky top-28">
            <div className="h-56 overflow-hidden border-b border-black/10">
              <img src={course.image} alt={course.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-7">
              <p className="font-display font-extrabold text-4xl text-ink">₹{course.price_inr.toLocaleString("en-IN")}</p>
              <p className="text-sm text-clay mt-1">One-time · {course.duration}</p>
              <button onClick={enroll} data-testid="enroll-btn"
                className="w-full mt-6 py-4 bg-ink text-rice font-medium hover:bg-cinnabar transition-colors duration-300">
                Enroll & Pay
              </button>
              <div className="mt-6 space-y-2.5 text-sm text-clay">
                {["Live & recorded sessions", "Assignments with teacher feedback", "AI tutor practice access", "Certificate on completion"].map((f, i) => (
                  <div key={i} className="flex items-center gap-2"><Check size={15} className="text-cinnabar" /> {f}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
